import React, { useCallback, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'
import useAudioPlayer from '../hooks/useAudioPlayer'
import { CapeeshColors } from '../../../../../assets/ColorPalette'
import renderFormattedText from '../utils/renderFormattedText'

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: '12px 0',
    },
    card: {
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 16,
        padding: '20px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        backdropFilter: 'blur(8px)',
        '@media (max-width: 599px)': {
            padding: '14px 14px',
            borderRadius: 12,
            gap: 10,
        },
    },
    wordText: {
        fontFamily: 'Rubik',
        fontSize: 22,
        fontWeight: 600,
        color: '#FFFFFF',
        textAlign: 'center',
        '@media (max-width: 599px)': { fontSize: 18 },
    },
    explanationText: {
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 400,
        lineHeight: '160%',
        color: 'rgba(255, 255, 255, 0.85)',
        textAlign: 'center',
    },
    listenButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 48,
        height: 48,
        borderRadius: '50%',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
    },
    icon: {
        fontSize: 24,
        color: '#FFFFFF',
    },
}))

/**
 * DefinitionInteraction — word + explanation + listen button (interactiveElementType === 10).
 * Non-scored; auto-completes after display.
 */
const DefinitionInteraction = ({ testOption, audioMap, onComplete, isNativeSpeaker = false }) => {
    const classes = useStyles()
    const audioPlayer = useAudioPlayer()
    const [hasCompleted, setHasCompleted] = useState(false)

    const blobUrl = testOption.textAudioUrl
        ? audioMap[testOption.textAudioUrl]
        : null

    const handleListen = useCallback(() => {
        if (blobUrl) {
            audioPlayer.playAudio(blobUrl).then(() => {
                if (!hasCompleted) {
                    setHasCompleted(true)
                    if (onComplete) {
                        onComplete({
                            testOptionId: testOption.testOptionId,
                            answers: [],
                        })
                    }
                }
            })
        }
    }, [blobUrl, audioPlayer, hasCompleted, testOption.testOptionId, onComplete])

    // Auto-complete if no audio
    React.useEffect(() => {
        if (!blobUrl && !hasCompleted) {
            setHasCompleted(true)
            if (onComplete) {
                onComplete({
                    testOptionId: testOption.testOptionId,
                    answers: [],
                })
            }
        }
    }, [])

    // Auto-play audio on mount
    React.useEffect(() => {
        if (blobUrl) {
            handleListen()
        }
    }, [])

    return (
        <div className={classes.container}>
            <div className={classes.card}>
                <Typography className={classes.wordText}>
                    {renderFormattedText(testOption.text)}
                </Typography>
                {!isNativeSpeaker && testOption.explanationText && (
                    <Typography className={classes.explanationText}>
                        {renderFormattedText(testOption.explanationText)}
                    </Typography>
                )}
                {blobUrl && (
                    <button className={classes.listenButton} onClick={handleListen}>
                        <VolumeUpIcon className={classes.icon} />
                    </button>
                )}
            </div>
        </div>
    )
}

export default DefinitionInteraction
